/**
 * Voiceflow chat is loaded ONLY when the user actively clicks the
 * "Kysy agentilta" launcher. The Voiceflow bundle script and CDN request
 * do NOT run on page load, before consent, or when the user just rejects
 * cookies. This keeps the site quiet for non-chat visitors.
 */

let bundleRequested = false;
let bundleReady = false;
let pendingResolvers = [];
let pixelHookInstalled = false;

const injectVfStyles = () => {
  if (typeof document === 'undefined') return;
  if (document.querySelector('style[data-vf-overrides]')) return;
  const styleEl = document.createElement('style');
  styleEl.setAttribute('data-vf-overrides', '');
  styleEl.textContent = `
    #voiceflow-chat,
    .vfrc-launcher,
    .vfrc-widget,
    [class*="LauncherButton"],
    [data-vf-id="launcher"] {
      transition: opacity 200ms ease, transform 200ms ease !important;
    }
    @media (max-width: 640px) {
      #voiceflow-chat .vfrc-launcher,
      .vfrc-launcher,
      [class*="LauncherButton"] {
        transform: scale(0.85) !important;
        transform-origin: bottom right !important;
      }
    }
    body[data-vf-hidden="true"] #voiceflow-chat,
    body[data-vf-hidden="true"] .vfrc-launcher,
    body[data-vf-hidden="true"] .vfrc-widget,
    body[data-vf-hidden="true"] [class*="LauncherButton"] {
      opacity: 0 !important;
      pointer-events: none !important;
      transform: translateY(20px) !important;
    }
  `;
  document.head.appendChild(styleEl);
};

const initWidget = () => {
  if (!window.voiceflow || !window.voiceflow.chat) return Promise.resolve();
  // load() returns a promise in widget-next; await it before calling open().
  const loadResult = window.voiceflow.chat.load({
    verify: { projectID: '69dd0d4b2ba6b506cadbc643' },
    url: 'https://general-runtime.voiceflow.com/',
    versionID: 'production',
    voice: { url: 'https://runtime-api.voiceflow.com/' },
    assistant: {
      title: 'Agencive AI',
      description: 'Kysy agentilta',
    },
    launch: {
      event: { type: 'launch', payload: {} },
      label: 'Kysy agentilta',
    },
  });
  const loadPromise = loadResult && typeof loadResult.then === 'function'
    ? loadResult
    : Promise.resolve();

  return loadPromise.then(() => {
    bundleReady = true;
    pendingResolvers.forEach((r) => r());
    pendingResolvers = [];

    if (pixelHookInstalled) return;
    pixelHookInstalled = true;
    const fireChatContact = () => {
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Contact', { content_name: 'AI Chat Open' });
      }
    };
    try {
      if (typeof window.voiceflow.chat.on === 'function') {
        window.voiceflow.chat.on('open', fireChatContact);
      }
      const origOpen = typeof window.voiceflow.chat.open === 'function'
        ? window.voiceflow.chat.open.bind(window.voiceflow.chat)
        : null;
      if (origOpen) {
        window.voiceflow.chat.open = function (...args) {
          fireChatContact();
          return origOpen(...args);
        };
      }
    } catch {
      /* ignore */
    }
  }).catch(() => {
    bundleReady = true;
    pendingResolvers.forEach((r) => r());
    pendingResolvers = [];
  });
};

export const ensureVoiceflowLoaded = () => new Promise((resolve) => {
  if (typeof window === 'undefined') { resolve(); return; }
  if (bundleReady) { resolve(); return; }
  pendingResolvers.push(resolve);
  if (bundleRequested) return;
  bundleRequested = true;
  injectVfStyles();
  const script = document.createElement('script');
  script.src = 'https://cdn.voiceflow.com/widget-next/bundle.mjs';
  script.type = 'text/javascript';
  script.async = true;
  script.onload = () => initWidget();
  document.head.appendChild(script);
});

export const openVoiceflowChat = async () => {
  await ensureVoiceflowLoaded();
  try {
    if (window.voiceflow && window.voiceflow.chat && typeof window.voiceflow.chat.open === 'function') {
      window.voiceflow.chat.open();
    }
  } catch {
    /* ignore */
  }
};
